import React from 'react';
import { Box, Stat, StatLabel, StatNumber, StatHelpText, Tooltip, Icon } from '@chakra-ui/react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { ArrowUpIcon, ArrowDownIcon, MinusIcon, InfoOutlineIcon } from '@chakra-ui/icons';

const getTrendIcon = (trend) => {
  if (trend === 'up') return <ArrowUpIcon color="green.400" ml={1} />;
  if (trend === 'down') return <ArrowDownIcon color="red.400" ml={1} />;
  return <MinusIcon color="gray.400" ml={1} />;
};

const getTrendText = (trend) => {
  if (trend === 'up') return 'Up';
  if (trend === 'down') return 'Down';
  return 'No change';
};

const getCardColors = (trend) => {
  if (trend === 'up') return { bg: 'green.50', border: 'green.200' };
  if (trend === 'down') return { bg: 'red.50', border: 'red.200' };
  return { bg: 'blue.50', border: 'blue.200' };
};

const Sparkline = ({ data, color = '#14b36e' }) => (
  <Box w="100%" h="46px">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
        <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  </Box>
);

const SummaryCard = ({ label, value, trend = 'neutral', tooltipText, children, sparklineData, sparklineColor, compact = false }) => {
  const { bg, border } = getCardColors(trend);
  return (
    <Box
      bg={bg}
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="2px"
      borderColor={border}
      p={compact ? 3 : 5}
      minW={compact ? '120px' : { base: '140px', md: '180px' }}
      minH={compact ? '90px' : '110px'}
      display="flex"
      alignItems="center"
      justifyContent="center"
      _hover={{ boxShadow: 'md', borderColor: trend === 'up' ? 'green.400' : trend === 'down' ? 'red.400' : 'blue.400' }}
      transition="box-shadow 0.2s, border-color 0.2s"
      role="group"
      style={{ background: `linear-gradient(135deg, ${bg} 80%, #fff 100%)` }}
    >
      <Stat>
        <StatLabel fontWeight="bold" fontSize={compact ? 'xs' : { base: 'sm', md: 'md' }}>
          <Tooltip label={tooltipText} hasArrow placement="top" isDisabled={!tooltipText}>
            <Box as="span" display="inline-flex" alignItems="center">
              {label}
              {tooltipText && (
                <Icon as={InfoOutlineIcon} ml={1} color="gray.400" _groupHover={{ color: 'blue.400' }} />
              )}
            </Box>
          </Tooltip>
        </StatLabel>
        <StatNumber fontSize={compact ? 'xl' : { base: '2xl', md: '3xl' }} fontWeight="extrabold">
          {value}
        </StatNumber>
        {children ? (
          <Box mt={1}>{children}</Box>
        ) : sparklineData ? (
          <Box mt={1}>
            <Sparkline data={sparklineData} color={sparklineColor} />
          </Box>
        ) : null}
        <StatHelpText fontSize={compact ? 'xs' : 'sm'} color={trend === 'up' ? 'green.500' : trend === 'down' ? 'red.500' : 'blue.500'}>
          {getTrendIcon(trend)} {getTrendText(trend)}
        </StatHelpText>
      </Stat>
    </Box>
  );
};

export default SummaryCard; 